import { useState, useRef, ReactNode } from 'react';
import type { AnySchema } from 'yup';
import { Formik, Form, FormikHelpers } from 'formik';
import clsx from 'clsx';

import type { FormVariables, FormData } from './ContactFormJSON.types';
import type { ContactFormData } from '../FormData/config';

// import Button from '@common/Button/Button';
import Fieldsets from './Fieldsets/Fieldsets';
import styles from './ContactForm.module.scss';

function setFormikInitialValues(formDataArr: { data: FormData[] }) {
  const initialValuesObj: Partial<ContactFormData> = {};
  formDataArr.data.forEach(formData => {
    formData.fields.forEach(field => {
      if (typeof field.defaultValue !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        initialValuesObj[field.name] = field.defaultValue;
      }
    });
  });
  return initialValuesObj as ContactFormData;
}

interface ContactFormProps {
  ContactFormJSON: {
    formId: string;
    formVariables: FormVariables;
    data: Array<FormData>;
  };
  contactFormValidationSchema: AnySchema;
  SuccessMessage: ReactNode;
}

export default function ContactForm(props: ContactFormProps) {
  const errorMessagesRef = useRef<HTMLDivElement>(null);
  const contactFormRef = useRef<HTMLDivElement>(null);
  const { ContactFormJSON, contactFormValidationSchema, SuccessMessage } =
    props;

  const [hasError, setHasError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  function resetHiddenDependentFormValues(values: Partial<ContactFormData>) {
    // reset fields if they are not visible
    ContactFormJSON.data.forEach(function (field) {
      let resetFieldValueBool = false;

      for (const visibleFieldSetDependency of field.visibleFieldsetDependencies) {
        // reset the value of the field if none of its dependencies are in the current form state
        if (
          values[visibleFieldSetDependency[0]] !=
            visibleFieldSetDependency[1] &&
          visibleFieldSetDependency[1] != 'default'
        ) {
          resetFieldValueBool = true;
        } else {
          // reset boolean to false and break out of loop
          resetFieldValueBool = false;
          break;
        }
      }

      if (resetFieldValueBool == true) {
        // loop over the fields and remove values for fields that are not present on the DOM based on dependencies
        field.fields.map(function (fieldItem) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          values[fieldItem.name] = '';
        });
      }
    });
  }

  // Format phone number pattern variations to universal pattern sent to backend
  function formatPhoneNumber(value: string) {
    let formattedPhoneNumber;
    formattedPhoneNumber = value ? value.replace(/\D/g, '') : '';
    if (formattedPhoneNumber) {
      if (formattedPhoneNumber.length === 11) {
        formattedPhoneNumber = `+${formattedPhoneNumber}`;
      } else if (formattedPhoneNumber.length === 10) {
        formattedPhoneNumber = `+1${formattedPhoneNumber}`;
      }
    }
    return formattedPhoneNumber;
  }

  function handleSubmit({
    values,
    actions,
  }: {
    values: any;
    actions: FormikHelpers<ContactFormData>;
  }) {
    const contactFormEndpoint = ContactFormJSON.formVariables.endpoint;
    const routingStatus = ContactFormJSON.formVariables.routingStatus;

    // reset values of form fields that are not visible because of dependencies
    resetHiddenDependentFormValues(values);

    // transform form data to acceptable format for backend
    const form_data = new FormData();
    for (const key in values) {
      if (key === 'phone_number') {
        values[key] = formatPhoneNumber(values[key]);
      }
      form_data.append(key, values[key]);
    }

    // append routing status
    form_data.append('routing_status', routingStatus);

    fetch(`${contactFormEndpoint + '/forms/process-form'}`, {
      method: 'POST',
      body: form_data,
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // successful submission
          setShowSuccess(true);
        } else {
          // error with submission
          if (data.message != 'Form Input Errors') {
            if (errorMessagesRef.current) {
              errorMessagesRef.current.textContent = data.message;
            }
          }

          // add error state for fields with errors
          for (const key in data.errors) {
            actions.setFieldError(key, data.errors[key]);
          }
          setHasError(true);
        }
        actions.setSubmitting(false);
        contactFormRef.current?.scrollIntoView();
      })
      .catch(error => {
        console.error(error);
        actions.setSubmitting(false);
        setHasError(true);
        contactFormRef.current?.scrollIntoView();
      });
  }

  return (
    <div ref={contactFormRef}>
      <Formik
        initialValues={setFormikInitialValues(ContactFormJSON)}
        validationSchema={contactFormValidationSchema}
        onSubmit={(values, actions) => {
          handleSubmit({ values, actions });
        }}
      >
        {({
          errors,
          touched,
          isSubmitting,
          isValid,
          setFieldTouched,
          setFieldValue,
        }) => (
          <>
            {/* Show success state */}
            {showSuccess && SuccessMessage}

            {/* Show default state */}
            {!showSuccess && (
              <Form id={ContactFormJSON.formId}>
                {hasError ? (
                  <div
                    data-testid="form__error"
                    className={clsx(styles.formErrors, 'form__error-message')}
                    ref={errorMessagesRef}
                  >
                    {ContactFormJSON.formVariables.errorMessage}
                  </div>
                ) : (
                  <div
                    data-testid="form__notice"
                    className={clsx(styles.formNotice, 'form__notice-message')}
                  >
                    {ContactFormJSON.formVariables.noticeMessage}
                  </div>
                )}
                <Fieldsets
                  formErrors={errors}
                  formTouched={touched}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  {...ContactFormJSON}
                />
                <button
                  data-testid="form__cta--submit"
                  type="submit"
                  className={clsx('form__submit-button')}
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    Object.keys(touched).length === 0
                      ? true
                      : false
                  }
                >
                  {!isSubmitting && 'Submit'}
                  {isSubmitting && 'Processing'}
                </button>
              </Form>
            )}
          </>
        )}
      </Formik>
    </div>
  );
}
